import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { formsAPI, clientsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Download, 
  Printer, 
  CheckCircle, 
  Loader2,
  FileText,
  Home,
  Eye
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function PDFPreviewPage() {
  const { clientId, formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [client, setClient] = useState(location.state?.client || null);
  const [formType, setFormType] = useState(location.state?.formType || null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [formId, clientId]);

  const loadData = async () => {
    try {
      // Load client if not passed via state
      if (!client) {
        const clientData = await clientsAPI.getById(clientId);
        setClient(clientData);
      }

      // Get the completed form data
      const completedForms = await formsAPI.getCompleted(clientId);
      const form = completedForms.find(f => f.id === formId);
      
      if (form) {
        setFormType(form.form_type);
        // Create PDF URL with token for viewing (use /view endpoint for inline display)
        const token = localStorage.getItem('token');
        setPdfUrl(`${API_BASE}/api/forms/completed/${formId}/pdf/view?token=${token}`);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!formId) return;
    
    const token = localStorage.getItem('token');
    const downloadUrl = `${API_BASE}/api/forms/completed/${formId}/pdf/download?token=${token}`;
    const fileName = `${getFormTypeName(formType).replace(/\s+/g, '_')}-${client?.last_name || 'Client'}.pdf`;
    
    try {
      toast.info('Downloading PDF...');
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 150);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed - opening in new tab');
      window.open(downloadUrl, '_blank');
    }
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    // Open in new tab for printing
    window.open(pdfUrl, '_blank');
  };

  const getFormTypeName = (type) => {
    const names = {
      'medical_release': 'Medical Release',
      'screening_form': 'Screening Form',
      'specialist_form': 'Specialist Form',
      'pcst_authorization': 'PCST Authorization',
      'client_transfer': 'Client Transfer',
      'acknowledgment': 'Acknowledgment'
    };
    return names[type] || type;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-center">
          <img src={AVIDA_LOGO} alt="Avida" className="h-16 mx-auto mb-4 animate-pulse" />
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-amber-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/clients/${clientId}/packet/complete`, { state: { client } })}
                className="text-blue-600 hover:text-blue-800 hover:bg-amber-100 rounded-xl"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src={AVIDA_LOGO} alt="Avida" className="h-10" />
              <div>
                <h1 
                  className="font-bold text-lg text-blue-900"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  PDF Preview
                </h1>
                <p className="text-sm text-blue-600">
                  {client?.first_name} {client?.last_name} - {getFormTypeName(formType)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="border-amber-300 text-blue-700 hover:bg-amber-100"
                data-testid="print-button"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                size="sm"
                onClick={handleDownload}
                className="btn-avida"
                data-testid="download-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Success Banner */}
      <div className="bg-green-500 text-white py-3">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Form completed and saved successfully!
          </span>
        </div>
      </div>

      {/* PDF Preview */}
      <main className="flex-1 p-4 md:p-8">
        <div className="max-w-5xl mx-auto">
          <Card className="overflow-hidden shadow-xl border-amber-200">
            {pdfUrl ? (
              <div className="bg-amber-50 p-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <FileText className="w-10 h-10 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-blue-900 mb-2">
                    {getFormTypeName(formType)}
                  </h2>
                  <p className="text-blue-600 mb-6">
                    {client?.first_name} {client?.last_name}
                  </p>
                  
                  <div className="flex gap-4 justify-center flex-wrap">
                    <Button 
                      onClick={handlePrint} 
                      size="lg"
                      variant="outline" 
                      className="h-14 px-8 border-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Eye className="w-5 h-5 mr-2" />
                      View PDF
                    </Button>
                    <Button 
                      onClick={handleDownload} 
                      size="lg"
                      className="h-14 px-8 btn-avida"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  
                  <p className="text-sm text-blue-400 mt-6">
                    Click "View PDF" to open in a new tab, or "Download PDF" to save to your device
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[50vh] text-blue-400 bg-amber-50">
                <FileText className="w-16 h-16 mb-4" />
                <p className="text-blue-600">PDF not available</p>
              </div>
            )}
          </Card>
        </div>
      </main>

      {/* Bottom Actions */}
      <div className="bg-white border-t border-amber-200 p-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/clients/${clientId}/packet/complete`, { state: { client } })}
            className="flex-1 h-12 border-2 border-amber-200 text-blue-800 font-semibold rounded-xl hover:bg-amber-50"
            data-testid="view-all-forms-button"
          >
            <Eye className="mr-2 h-5 w-5" />
            View All Completed Forms
          </Button>
          <Button
            onClick={() => navigate('/clients')}
            className="flex-1 h-12 btn-avida"
            data-testid="back-to-clients-button"
          >
            <Home className="mr-2 h-5 w-5" />
            Back to Clients
          </Button>
        </div>
      </div>
    </div>
  );
}
